import {Card, Flag} from 'platform/components';
import {Align, Box, Center, Heading, HStack, Image, Show, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {AvoidBreakInside} from '../../../../components/AvoidBreakInside/AvoidBreakInside';
import i18n from '../../../../i18n/i18n';
import {PriceReportType} from '../../../../types/PriceReportType';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {AttractivenessChart} from './components/AttractivenessChart';
import {AttractivenessDynamicsAlert} from './components/AttractivenessDynamicsAlert';
import {AttractivenessSituationAlert} from './components/AttractivenessSituationAlert';

interface AttractivenessCardProps extends TestIdProps {
  priceReport?: PriceReportType;
}

export function AttractivenessCard(props: AttractivenessCardProps) {
  const {isPrint} = useIsPrintForced();
  const attractiveness = props.priceReport?.attractiveness;

  if (isNil(attractiveness) && isPrint) {
    return null;
  }

  const score = isNil(attractiveness) ? undefined : attractiveness.attractiveness_score * 100;
  const isBlacklisted = isNil(score) ? false : score <= 20;
  const isHitlisted = isNil(score) ? false : score >= 80;

  return (
    <AvoidBreakInside>
      <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
        <Box padding={2}>
          <VStack spacing={6}>
            <Align center>
              <Heading size={3} data-testid={suffixTestId('attractivenessTitle', props)}>
                {i18n.t('entity.attractivenessCard.title')}
              </Heading>
            </Align>

            <Text
              align="center"
              size="small"
              color="secondary"
              data-testid={suffixTestId('attractivenessDescription', props)}
            >
              {i18n.t(`entity.attractivenessCard.${isNil(score) ? 'unknown' : 'description'}`)}
            </Text>

            <Show when={isBlacklisted}>
              <Box
                backgroundColor="palettes.red.20.100"
                borderRadius="small"
                padding={4}
                data-testid={suffixTestId('attractivenessBlacklistAlert', props)}
              >
                <HStack spacing={4} align="center">
                  <Center width={8} height={8}>
                    <Image
                      src="assets/images/handbrake.svg"
                      aria-hidden
                      data-testid={suffixTestId('attractivenessBlacklistIcon', props)}
                    />
                  </Center>
                  <VStack spacing={1} grow={1} basis={0}>
                    <Flag
                      label={i18n.t('entity.attractivenessCard.blacklistFlag')}
                      colorScheme="red"
                      data-testid={suffixTestId('attractivenessBlacklistFlag', props)}
                    />
                    <Text
                      size="small"
                      data-testid={suffixTestId('attractivenessBlacklistDescription', props)}
                    >
                      {i18n.t('entity.attractivenessCard.blacklistDescription')}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </Show>

            <Show when={isHitlisted}>
              <Box
                backgroundColor="palettes.green.20.100"
                borderRadius="small"
                padding={4}
                data-testid={suffixTestId('attractivenessHitlistAlert', props)}
              >
                <HStack spacing={4} align="center">
                  <Center minWidth={8}>
                    <Image
                      src="assets/images/thumbs_up.svg"
                      width={8}
                      height={8}
                      aria-hidden
                      data-testid={suffixTestId('attractivenessHitlistIcon', props)}
                    />
                  </Center>
                  <VStack spacing={1} grow={1} basis={0}>
                    <Flag
                      label={i18n.t('entity.attractivenessCard.hitlistFlag')}
                      colorScheme="green"
                      data-testid={suffixTestId('attractivenessHitlistFlag', props)}
                    />
                    <Text
                      size="small"
                      data-testid={suffixTestId('attractivenessHitlistDescription', props)}
                    >
                      {i18n.t('entity.attractivenessCard.hitlistDescription')}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </Show>

            <AttractivenessChart
              value={score}
              data-testid={suffixTestId('attractivenessChart', props)}
            />

            <VStack spacing={2}>
              <AttractivenessSituationAlert
                situation={attractiveness?.market_situation}
                data-testid={suffixTestId('attractivenessSituationAlert', props)}
              />
              <AttractivenessDynamicsAlert
                dynamics={attractiveness?.price_dynamics}
                data-testid={suffixTestId('attractivenessDynamicsAlert', props)}
              />
            </VStack>
          </VStack>
        </Box>
      </Card>
    </AvoidBreakInside>
  );
}
